/**
 * DateTimeEditorV16 - Custom datetime picker editor for Handsontable v16
 *
 * Extends TextEditor from Handsontable v16
 * Provides native datetime-local input for better UX
 */
import type Handsontable from 'handsontable-v16';
import { TextEditor } from 'handsontable-v16/editors';
import { textRenderer } from 'handsontable-v16/renderers';
import { registerCellType } from 'handsontable-v16/cellTypes';
import type { CellProperties } from 'handsontable-v16/settings';
import { _isNotEmpty, _notNil, uuid } from '@/littledash';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import '../editors/DateTimeEditor.scss';

interface DateTimeCellPropertiesV16 extends CellProperties {
  isDateOnly?: boolean;
  displaySeconds?: boolean;
  restrictFutureDates?: boolean;
}

/**
 * Custom renderer for datetime cells v16
 */
export const datetimeRendererV16 = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  prop: string | number,
  value: unknown,
  cellProperties: CellProperties
) => {
  let val = '';

  if (_isNotEmpty(value)) {
    const cellProps = cellProperties as DateTimeCellPropertiesV16;
    const isDateOnly = cellProps?.isDateOnly ?? false;
    const valueStr = typeof value === 'string' ? value : '';
    if (isDateOnly === true) {
      val = DateUtils.renderDate(valueStr, { defaultResponse: valueStr });
    } else {
      val = DateUtils.renderDateTime(valueStr, { defaultResponse: valueStr });
    }
  }

  textRenderer(hotInstance, td, row, column, prop, val, cellProperties);
  return td;
};

/**
 * Validator for datetime editor v16
 */
export const dateTimeValidatorV16 = (value: unknown, callback: (valid: boolean) => void) => {
  callback(DateUtils.isValidDateTime(value as string));
};

/**
 * DateTimeEditorV16 - Custom datetime picker editor
 *
 * Extends TextEditor from Handsontable v16
 * Provides native datetime-local input for better UX
 */
export class DateTimeEditorV16 extends TextEditor {
  static type = 'datetime-v16';
  static readonly dateTimeEditorId = Object.freeze(`${uuid()}`);
  private _initialValue?: unknown;
  private _isOpened = false;
  private _isDestroyed = false;

  /**
   * Register the cell type with Handsontable v16
   */
  static register(type = DateTimeEditorV16.type) {
    registerCellType(type, {
      editor: DateTimeEditorV16 as typeof TextEditor,
      renderer: datetimeRendererV16,
      validator: dateTimeValidatorV16,
    });
  }

  // Private helper methods for DOM elements
  #container(): HTMLDivElement | null {
    return this.hot.rootDocument.getElementById(
      `${DateTimeEditorV16.dateTimeEditorId}-datetime-container`
    ) as HTMLDivElement | null;
  }

  #overlay(): HTMLDivElement | null {
    return this.hot.rootDocument.getElementById(
      `${DateTimeEditorV16.dateTimeEditorId}-datetime-overlay`
    ) as HTMLDivElement | null;
  }

  #input(): HTMLInputElement | null {
    return this.hot.rootDocument.getElementById(
      `${DateTimeEditorV16.dateTimeEditorId}-datetime-input`
    ) as HTMLInputElement | null;
  }

  init() {
    super.init();
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  /**
   * Override prepare to avoid v16 internal API issues
   * Do NOT call super.prepare() as it may call methods that don't exist in v16
   */
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: CellProperties
  ) {
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.originalValue = originalValue;
    this.cellProperties = cellProperties;

    // Store initial value in private variable (protected from Handsontable's internal modifications)
    this._initialValue = originalValue;
  }

  setValue(newValue: string) {
    const cellProps = this.cellProperties as DateTimeCellPropertiesV16;
    const isDateOnly = cellProps?.isDateOnly ?? false;
    const pattern = isDateOnly === true ? DateUtils.isoDatePattern : DateUtils.isoDateTimePattern;

    super.setValue(pattern.test(newValue) ? newValue : '');
  }

  open() {
    // Create elements if not yet created
    if (_notNil(this.#container()) === false) {
      this.createElements();
    }
    this._isOpened = true;

    // Sync TEXTAREA with current cell value (super.open() is skipped, so this must be done manually)
    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.value = typeof this._initialValue === 'string' ? (this._initialValue as string) : '';
    }

    // Don't call super.open() to avoid v16 internal issues
    this.display();
  }

  close() {
    if (!this._isOpened) {
      return;
    }
    this._isOpened = false;
    this.display(false);
    // Don't call super.close() to avoid v16 internal issues
    if (!this._isDestroyed) {
      this.hot.render();
    }
  }

  /**
   * Begin editing
   */
  beginEditing() {
    this.open();
  }

  /**
   * Focus the editor
   */
  focus() {
    const dateTimeInput = this.#input();
    if (_notNil(dateTimeInput)) {
      dateTimeInput.focus();
    }
  }

  isOpened() {
    return this._isOpened;
  }

  display(display = true) {
    const cellProps = this.cellProperties as DateTimeCellPropertiesV16;
    const restrictFutureDates = cellProps?.restrictFutureDates ?? false;
    const dateTimeContainer = this.#container();
    const dateTimeOverlay = this.#overlay();
    const dateTimeInput = this.#input();

    if (_notNil(dateTimeContainer) && _notNil(dateTimeOverlay) && _notNil(dateTimeInput)) {
      if (display === true) {
        const isDateOnly = cellProps?.isDateOnly ?? false;
        const cellBorder = { vertical: 3, horizontal: 2 };
        const offset = this.TD.getBoundingClientRect();

        dateTimeOverlay.style.top = `${offset.top + cellBorder.vertical}px`;
        dateTimeOverlay.style.left = `${offset.left + cellBorder.horizontal}px`;

        if (this.TEXTAREA instanceof HTMLTextAreaElement) {
          this.TEXTAREA.style.width = `${offset.width}px`;
          this.TEXTAREA.style.height = `${offset.height - 5}px`;
        }

        dateTimeContainer.style.visibility = 'visible';
        dateTimeInput.value = DateInputUtils.toLocalDateTime(this.getValue(), {
          includeSeconds: cellProps?.displaySeconds ?? false,
        });
        dateTimeInput.type = isDateOnly === true ? 'date' : 'datetime-local';
        dateTimeInput.max = restrictFutureDates === true ? DateUtils.dateNow() : '';

        requestAnimationFrame(() => {
          (dateTimeInput as HTMLInputElement & { showPicker?: () => void })?.showPicker?.();
        });
      } else {
        dateTimeContainer.style.visibility = 'hidden';
      }
    }
  }

  createElements() {
    super.createElements();

    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.style.fontSize = '1px';
      this.TEXTAREA.style.opacity = '0';
    }

    const datetimeContainer = this.hot.rootDocument.createElement('div');
    const datetimeOverlay = this.hot.rootDocument.createElement('div');
    const datetimeInput = this.hot.rootDocument.createElement('input');

    const handleChangeOrMouseDown = () => {
      if (this._isDestroyed) {
        return;
      }
      this.setValue(DateInputUtils.localDateTimeToISODateTime(datetimeInput.value));
      this.finishEditing(false, false);
      const cellProps = this.cellProperties as { row?: number; col?: number };
      const { row, col } = cellProps;
      if (row !== undefined && col !== undefined) {
        this.hot.selectCell(row, col);
      }
    };

    datetimeContainer.id = `${DateTimeEditorV16.dateTimeEditorId}-datetime-container`;
    datetimeContainer.classList.add('dateTimeContainer');
    datetimeContainer.style.visibility = 'hidden';
    datetimeContainer.setAttribute('data-test-component', 'DateTimeEditor');
    datetimeContainer.setAttribute('data-test-element', 'container');
    datetimeContainer.addEventListener('mousedown', handleChangeOrMouseDown, { passive: true });

    datetimeOverlay.id = `${DateTimeEditorV16.dateTimeEditorId}-datetime-overlay`;
    datetimeOverlay.classList.add('dateTimeOverlay');
    datetimeContainer.appendChild(datetimeOverlay);

    datetimeInput.id = `${DateTimeEditorV16.dateTimeEditorId}-datetime-input`;
    datetimeInput.type = 'datetime-local';
    datetimeInput.classList.add('dateTimeInput');
    datetimeInput.setAttribute('data-test-element', 'datetime-input');
    const cellProps = this.cellProperties as DateTimeCellPropertiesV16;
    datetimeInput.value = DateInputUtils.toLocalDateTime(this.getValue(), {
      includeSeconds: cellProps?.displaySeconds ?? false,
    });
    datetimeInput.addEventListener('change', handleChangeOrMouseDown, { passive: true });

    datetimeInput.addEventListener('keydown', (e) => {
      if (this._isDestroyed) {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.finishEditing(true);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleChangeOrMouseDown();
      }
    });

    datetimeOverlay.appendChild(datetimeInput);
    this.hot.rootDocument.body.appendChild(datetimeContainer);
  }

  destroyElements() {
    this._isDestroyed = true;
    const datetimeContainer = this.#container();
    if (_notNil(datetimeContainer)) {
      datetimeContainer.remove();
    }
  }

  getValue() {
    return this.TEXTAREA instanceof HTMLTextAreaElement ? this.TEXTAREA.value : '';
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false) {
    // Check if Handsontable instance is destroyed before any operations
    if (this._isDestroyed) {
      return;
    }

    if (!this._isOpened) {
      try {
        super.finishEditing(restoreOriginalValue, ctrlDown);
      } catch {
        // Instance may have been destroyed, silently fail
      }
      return;
    }

    this.close();

    // Re-check if Handsontable instance is still valid AFTER close()
    if (this._isDestroyed) {
      return;
    }

    try {
      if (restoreOriginalValue) {
        // Use _initialValue instead of this.originalValue (which gets overwritten by Handsontable)
        const valueToRestore = this._initialValue ?? this.originalValue;
        if (valueToRestore !== undefined && valueToRestore !== null) {
          this.hot.setDataAtCell(this.row, this.col, valueToRestore);
        }
      } else {
        // Save the new value
        const currentValue = this.getValue();
        if (currentValue !== this._initialValue) {
          this.hot.setDataAtCell(this.row, this.col, currentValue);
        }
      }
    } catch {
      // Instance may have been destroyed during the operation
      // Silently fail to avoid console errors
    }

    // Final check before calling super
    if (this._isDestroyed) {
      return;
    }

    try {
      super.finishEditing(restoreOriginalValue, ctrlDown);
    } catch {
      // Instance may have been destroyed, silently fail
    }
  }
}
